import {
  QueryClient,
  QueryCache,
  MutationCache,
  keepPreviousData,
  type Query,
  type QueryFunction,
} from "@tanstack/react-query";
import { errorShownByPanel } from "@/lib/shownErrors";
import {
  ApiError,
  describeApiError,
  isUnreachable,
  parseApiError,
  GENERIC_MESSAGE,
  localizeApiMessage,
  SECURE_REQUEST_FAILED_MESSAGE,
  SERVER_FAULT_MESSAGE,
  STEP_UP_DECLINED_MESSAGE,
  TIMEOUT_MESSAGE,
  UNREACHABLE_MESSAGE,
} from "./apiError";
import { toast } from "@/hooks/use-toast";
import { interpolate, msg, type Msg, type MsgVars } from "@shared/i18n";
import { SESSION_IDLE_CODE, SESSION_ABSOLUTE_CODE } from "@shared/sessionPolicy";
import { requestStepUp } from "./stepUp";
import { isKnownSignedIn, recordSessionEnd, rememberReturnTo } from "./sessionEnd";
import { isPublicPagePath } from "./publicPages";

export { ApiError, describeApiError, isUnreachable, isUnauthenticated } from "./apiError";
export type { ApiErrorKind } from "./apiError";

/**
 * THE THROWN MESSAGE IS WHAT ~95 TOASTS PUT IN FRONT OF A USER.
 *
 * This used to be `new Error(\`${status}: ${text}\`)` and nothing else, so any
 * `toast({ description: error.message })` rendered the wire format verbatim:
 *
 *   500: {"message":"Email service is not configured. Please contact support."}
 *
 * And a request that never reached the server threw the browser's own words —
 * "Failed to fetch", "Load failed" — into the same toasts.
 *
 * Every failure is now an `ApiError` (lib/apiError.ts): `message` is already
 * the sentence to show (`describeApiError`), `kind` says whether the server
 * answered at all, and the wire format stays on `raw` for `parseApiError`, so a
 * form that marks the offending input keeps working.
 */
export type ApiRequestError = ApiError;

/** Where a person sets up the passkey or authenticator their company requires. */
export const STRONG_SETUP_PAGE = "/account/security/passkeys";

/** Build the error for a response the server answered with a failure. */
function httpError(status: number, body: string): ApiError {
  const raw = `${status}: ${body}`;
  const parsed = parseApiError(new Error(raw), status >= 500 ? SERVER_FAULT_MESSAGE : GENERIC_MESSAGE);
  // Only reachable once the in-place prompt was declined or could not be shown:
  // say what to do, and that nothing happened, rather than "2FA verification
  // required".
  if (parsed.code === "2FA_REQUIRED") {
    return new ApiError(STEP_UP_DECLINED_MESSAGE, { status, code: parsed.code, kind: "http", raw });
  }
  // The company requires a passkey or an authenticator app and this person has
  // neither (WS5-B5): every page is closed until they set one up, so go to the
  // page that does — with a real page load, once.
  if (parsed.code === "STRONG_MFA_SETUP" && typeof window !== "undefined" && window.location.pathname !== STRONG_SETUP_PAGE) {
    window.location.assign(`${STRONG_SETUP_PAGE}?required=1`);
  }
  const provisional = new ApiError(parsed.message, { status, code: parsed.code ?? "", kind: "http", raw });
  // The sentence a user sees is decided in ONE place — describeApiError — so a
  // bare "Not authenticated" or "Internal Server Error" never reaches a toast.
  return new ApiError(describeApiError(provisional), { status, code: parsed.code ?? "", kind: "http", raw });
}

async function errorFrom(res: Response): Promise<ApiError> {
  const text = (await res.clone().text().catch(() => "")) || res.statusText;
  return httpError(res.status, text);
}

function timeoutError(): ApiError {
  return new ApiError(TIMEOUT_MESSAGE, { kind: "timeout", raw: "timeout" });
}

/** A deadline that tripped somewhere other than ours — `AbortSignal.timeout`, the platform. */
function isTimeoutAbort(err: unknown): boolean {
  return (err as { name?: string } | null)?.name === "TimeoutError";
}

function networkError(cause: unknown): ApiError {
  const engine = (cause as Error)?.message || "network error";
  return new ApiError(UNREACHABLE_MESSAGE, { kind: "network", raw: engine });
}

/** The server's code for a response, without consuming the body. */
async function peekBody(res: Response): Promise<{ code: string; message: string }> {
  try {
    const body = await res.clone().json();
    return {
      code: typeof body?.code === "string" ? body.code : typeof body?.error === "string" ? body.error : "",
      message: typeof body?.message === "string" ? body.message : "",
    };
  } catch {
    return { code: "", message: "" };
  }
}

/**
 * A RATE-LIMITED APP LOOKED LIKE AN EMPTY ONE.
 *
 * `generalApiLimiter` allows a fixed number of requests per minute, and nothing
 * on the client used to understand 429: a throttled query threw, and every call
 * site defaults its data to `[]` — so the app rendered signed-in with no
 * projects, no contacts and no menu, with no explanation.
 *
 * Say so, once, rather than silently emptying the screen. Throttled because a
 * page load that trips the limiter trips it on many requests at once.
 */
/*
 * Reads and writes keep separate clocks (QA, 2026-10-28). A refused READ is
 * said once a minute at most — the server's window — however many queries a
 * page had in flight: ordinary navigation produced a run of "Too many
 * requests" toasts at the old ten seconds. A refused WRITE is always said
 * (a save that did not happen must never pass in silence), and a read's
 * notice moments earlier cannot swallow it, which the one shared clock did.
 */
const RATE_NOTICE_GAP_MS = { read: 60_000, write: 10_000 } as const;
const lastRateLimitNotice = { read: 0, write: 0 };

/**
 * A notice raised from here, outside any component, in the reader's language.
 *
 * Resolved to a plain string on purpose: the toaster merges two identical
 * plain-text notices into one, and an element (a component calling the hook)
 * would defeat that. It reads what `useTranslation` already cached — the
 * language LanguageProvider put on <html data-ui-language>, and that language's registry
 * rows under the hook's own query key — and falls back to the English.
 */
function say(m: Msg, vars?: MsgVars): string {
  const lang = typeof document === "undefined" ? "en" : document.documentElement.dataset.uiLanguage;
  if (!lang || lang === "en") return interpolate(m.en, vars);
  const rows = queryClient.getQueryData<Record<string, string>>(["/api/translations/language", lang]);
  const v = rows?.[m.key];
  return interpolate(typeof v === "string" && v ? v : m.en, vars);
}

export function notifyRateLimited(kind: "read" | "write" = "read"): boolean {
  const now = Date.now();
  if (now - lastRateLimitNotice[kind] < RATE_NOTICE_GAP_MS[kind]) return false;
  lastRateLimitNotice[kind] = now;
  toast({
    variant: "destructive",
    title: say(msg("shellErrorTooManyRequestsTitle", "Too Many Requests")),
    description: say(
      msg(
        "shellErrorRateLimitedNotice",
        "Too many requests from this network — wait a minute and try again. Some content may be missing until then.",
      ),
    ),
  });
  return true;
}

/* ──────────────────────────────── csrf ──────────────────────────────── */

let csrfToken: string | null = null;

/**
 * The token fetch in flight, so concurrent writes share ONE request.
 *
 * Two saves at once used to fetch two tokens. Each mint answers with its own
 * token and rewrites the `__csrf` cookie, so whichever request held the loser's
 * token posted a header that no longer matched — a 403 on a save that did
 * nothing wrong.
 */
let csrfInFlight: Promise<string> | null = null;

/**
 * A usable token, or an ApiError. NEVER an empty string.
 *
 * A failed fetch used to come back as `''`, and the write then went out with no
 * header at all — a guaranteed 403 that read as a permissions problem. If there
 * is no token, the request that needed one fails as what it is: unreachable.
 */
async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  if (csrfInFlight) return csrfInFlight;
  csrfInFlight = (async () => {
    const d = deadline(REQUEST_TIMEOUT_MS);
    let res: Response;
    try {
      res = await fetch("/api/csrf-token", { credentials: "include", signal: d.signal });
    } catch (err) {
      throw d.timedOut() || isTimeoutAbort(err) ? timeoutError() : networkError(err);
    } finally {
      d.release();
    }
    if (!res.ok) throw await errorFrom(res);
    const data = await res.json().catch(() => null);
    const token = data?.csrfToken;
    if (typeof token !== "string" || !token) {
      throw new ApiError(SECURE_REQUEST_FAILED_MESSAGE, {
        status: res.status,
        code: "CSRF_UNAVAILABLE",
      });
    }
    csrfToken = token;
    return token;
  })();
  try {
    return await csrfInFlight;
  } finally {
    csrfInFlight = null;
  }
}

/** Drop the cached token so the next write mints a fresh one. */
export function resetCsrfToken(): void {
  csrfToken = null;
}

/**
 * The browser-called paths the server exempts from the token
 * (server/lib/csrf.ts CSRF_EXEMPT_PATHS). They run before a session exists, so
 * minting a token for them is a wasted round trip in front of signing in — and
 * a token fetch that fails on a bad connection would then block a sign-in that
 * never needed one. Drift is survivable: a CSRF rejection is retried WITH a
 * token regardless of this list.
 */
const CSRF_EXEMPT = new Set([
  "/api/auth/login",
  "/api/auth/passkeys/authenticate/options",
  "/api/auth/passkeys/authenticate/verify",
  "/api/csrf-token",
]);

const pathOf = (url: string) => url.split("?")[0];

function isCsrfExempt(url: string): boolean {
  const p = pathOf(url);
  return CSRF_EXEMPT.has(p) || p.startsWith("/api/public/");
}

/**
 * Requests that change WHICH session this is. The token is bound to the
 * session id, so the one cached before signing in is dead the moment the
 * server regenerates the session; dropping it here saves the first write
 * afterwards a guaranteed 403-and-retry.
 */
const SESSION_CHANGING = new Set([
  "/api/auth/login",
  "/api/auth/logout",
  "/api/auth/passkeys/authenticate/verify",
  "/api/auth/link-account",
]);

/**
 * Did the server reject the CSRF token specifically?
 *
 * Only this is worth a retry. The old rule — any 403 whose body mentioned
 * "csrf" — also re-sent legitimate refusals.
 */
async function isCsrfRejection(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  const { code, message } = await peekBody(res);
  return code === "CSRF_INVALID" || code === "EBADCSRFTOKEN" || /invalid csrf token/i.test(message);
}

/* ────────────────────────────── deadlines ────────────────────────────── */

/**
 * NOTHING IN THIS CLIENT USED TO TIME OUT.
 *
 * `fetch` has no deadline of its own, so a backend that accepts the connection
 * and then stalls — a cold serverless function, a wedged pooler connection —
 * left the promise pending for ever. A deadline turns that into an ordinary
 * error that the screen can show and the user can retry. Two ceilings, because
 * they are different jobs: an ordinary API call that has not answered in 30s is
 * not going to, and an upload of a 25 MB site plan legitimately takes minutes.
 *
 * `timeoutMs: 0` opts out entirely, for the streaming chat endpoint whose whole
 * behaviour is to hold the response open.
 */
export const REQUEST_TIMEOUT_MS = 30_000;
export const UPLOAD_TIMEOUT_MS = 180_000;

/**
 * A signal that trips on our deadline AND relays the caller's own abort — React
 * Query's cancellation, or a stop button. Built per attempt: an AbortSignal is
 * single-use, so reusing one would abort a retry the instant the original
 * deadline passed. `release` clears the timer so it never outlives the request.
 */
function deadline(ms: number, caller?: AbortSignal | null) {
  if (ms <= 0 || typeof AbortController === "undefined") {
    return { signal: caller ?? undefined, release: () => {}, timedOut: () => false };
  }
  const ctl = new AbortController();
  let fired = false;
  const timer = setTimeout(() => {
    fired = true;
    ctl.abort(
      typeof DOMException !== "undefined" ? new DOMException("timeout", "TimeoutError") : undefined,
    );
  }, ms);
  const relay = () => ctl.abort(caller?.reason);
  if (caller) {
    if (caller.aborted) relay();
    else caller.addEventListener("abort", relay, { once: true });
  }
  return {
    signal: ctl.signal,
    release: () => {
      clearTimeout(timer);
      caller?.removeEventListener("abort", relay);
    },
    timedOut: () => fired,
  };
}

/* ────────────────────────────── the door ────────────────────────────── */

export interface ApiRequestOptions {
  /** Override the deadline. 0 disables it (streaming responses only). */
  timeoutMs?: number;
  /**
   * A caller's own signal — React Query's, or a stop button. Combined with the
   * deadline rather than replacing it.
   */
  signal?: AbortSignal;
  /**
   * This call runs whether or not anyone is signed in, so its failure is never
   * evidence about the session: a 401 does not sign anyone out, a 2FA 403 does
   * not raise the step-up prompt, and a 429 does not toast. For background work
   * — page-view logging, repairs, best-effort pings — that must never be the
   * thing that sends somebody to the sign-in page.
   */
  authOptional?: boolean;
  /** Alias of `authOptional`, for call sites where "background" reads better. */
  background?: boolean;
  /**
   * How a 2FA 403 is met. Default: a write asks every time ("interactive"), a
   * read respects the minute of quiet after "Not now" ("passive"). A write the
   * USER did not ask for — the idle-clock ping — passes "passive", so it cannot
   * re-open a prompt somebody has just dismissed.
   */
  stepUp?: "interactive" | "passive";
}

type StepUpMode = "interactive" | "passive" | "never";

const isMutationMethod = (method: string) =>
  ["POST", "PUT", "DELETE", "PATCH"].includes(method.toUpperCase());

/**
 * One request, through CSRF, the deadline, the CSRF retry and the in-place
 * step-up. Returns the final Response for the caller to judge; throws only an
 * ApiError for "nothing answered" (or the caller's own abort, untouched).
 */
async function execute(
  method: string,
  url: string,
  body: BodyInit | undefined,
  isForm: boolean,
  opts: { timeoutMs: number; signal?: AbortSignal; stepUp: StepUpMode },
): Promise<Response> {
  const needsCsrf = isMutationMethod(method) && !isCsrfExempt(url);

  /**
   * Each attempt builds its OWN headers and its own deadline, and shares
   * nothing with the one before it. FormData sets no Content-Type on purpose —
   * writing it by hand strips the multipart boundary and the server cannot
   * parse the body.
   */
  const attempt = async (token: string): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
    if (token) headers["csrf-token"] = token;
    const d = deadline(opts.timeoutMs, opts.signal);
    try {
      return await fetch(url, { method, headers, body, credentials: "include", signal: d.signal });
    } catch (err) {
      // The caller cancelled (React Query unmounting, a stop button): not a
      // failure of ours to classify, and React Query expects its own abort.
      const timedOut = d.timedOut() || isTimeoutAbort(err);
      if (opts.signal?.aborted && !timedOut) throw err;
      throw timedOut ? timeoutError() : networkError(err);
    } finally {
      d.release();
    }
  };

  let token = needsCsrf ? await getCsrfToken() : "";
  let res = await attempt(token);

  // A stale CSRF token: mint a fresh one and retry, once. Only when the SERVER
  // says the token was the problem. The cache is cleared only if it still holds
  // the token this request sent — a parallel request may already have minted
  // the replacement, and throwing that away would rewrite the cookie under it.
  if (await isCsrfRejection(res)) {
    if (!token || csrfToken === token) csrfToken = null;
    token = await getCsrfToken();
    res = await attempt(token);
  }

  // The session owes its second factor again (the two-hour window lapsed).
  // Prove it in place and send the SAME request again — see lib/stepUp.ts.
  if (opts.stepUp !== "never" && res.status === 403 && (await is2FARequired(res))) {
    const outcome = isOnPublicRoute()
      ? "suppressed"
      : await requestStepUp({ interactive: opts.stepUp === "interactive" });
    if (outcome === "verified") {
      res = await attempt(token);
    } else if (outcome === "unavailable") {
      // No dialog mounted — the old full-page route is still better than a
      // screen whose every list is silently empty.
      redirectTo2FA();
    }
  }

  return res;
}

/* ───────────────────────────── held saves ───────────────────────────── */

/**
 * The server's answer while its database is read-only for a cutover — the last
 * minute of a move between databases (server/lib/dbReadOnly.ts). The request
 * did nothing and is safe to send again, so it is sent again, quietly, with
 * backoff: the person sees a spinner rather than an error and a form to fill in
 * twice. Past the budget the normal error surfaces.
 *
 * Only this code. `db_read_only_midway` is a save that started running before
 * it was refused and may already have sent an email or pushed to SaleFish, so
 * it is never repeated behind anyone's back: it takes the ordinary error path.
 */
export const DB_READ_ONLY = "db_read_only";
export const DB_READ_ONLY_MIDWAY = "db_read_only_midway";
/** How long a request is held before its failure is shown. */
export const HOLD_BUDGET_MS = 90_000;
const HOLD_DELAY_CAP_MS = 8_000;

function holdFor(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** The `error` code of a JSON answer, read from a copy so the caller can still read the body. */
async function peekCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { error?: unknown } | null;
    return typeof body?.error === "string" ? body.error : null;
  } catch {
    return null;
  }
}

/** Send `again` while `res` says the database is read-only: 1s, 2s, 4s, 8s, 8s… within the budget. */
export async function holdWhileReadOnly(
  res: Response,
  again: () => Promise<Response>,
  signal?: AbortSignal | null,
): Promise<Response> {
  const started = Date.now();
  for (let attempt = 0; res.status === 503 && (await peekCode(res)) === DB_READ_ONLY; attempt++) {
    const wait = Math.min(1000 * 2 ** attempt, HOLD_DELAY_CAP_MS);
    if (Date.now() - started + wait > HOLD_BUDGET_MS) break;
    await holdFor(wait, signal);
    res = await again();
  }
  return res;
}

/**
 * THE ONE DOOR FOR EVERY REQUEST THIS APP MAKES.
 *
 * Using it is not a style preference. The server applies CSRF double-submit to
 * every authenticated POST/PUT/PATCH/DELETE (`server/routes/auth.ts`), so a
 * mutation written as a bare `fetch` is not merely inconsistent — it is
 * rejected with `403 invalid csrf token`, every time, in every environment.
 * That is how twenty-one writes shipped broken on 2026-09-10.
 * `scripts/api-client-lint.mjs` is a hard gate that keeps the next one from
 * shipping. Do not reintroduce a raw `fetch` for a mutation.
 *
 * FormData is passed through untouched — setting Content-Type by hand on a
 * multipart body strips the boundary and the server cannot parse it.
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options: ApiRequestOptions = {},
): Promise<Response> {
  const isForm = typeof FormData !== "undefined" && data instanceof FormData;
  const body =
    data === undefined || data === null ? undefined : isForm ? (data as FormData) : JSON.stringify(data);
  const timeoutMs = options.timeoutMs ?? (isForm ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS);
  const authOptional = Boolean(options.authOptional ?? options.background);

  const send = () =>
    execute(method, url, body, isForm, {
      timeoutMs,
      signal: options.signal,
      stepUp: authOptional ? "never" : options.stepUp ?? (isMutationMethod(method) ? "interactive" : "passive"),
    });
  // A cutover's read-only minute: sent again until the database is back (see above).
  const res = await holdWhileReadOnly(await send(), send, options.signal);

  if (res.status === 401 && !authOptional && !isAuthProbe(url)) {
    await handleSessionEnded(res);
    throw await errorFrom(res);
  }

  if (!res.ok) {
    if (res.status === 429 && !authOptional) notifyRateLimited(isMutationMethod(method) ? "write" : "read");
    throw await errorFrom(res);
  }

  if (SESSION_CHANGING.has(pathOf(url))) resetCsrfToken();
  return res;
}

/**
 * GET through the same door, for the handful of reads that are deliberately
 * outside React Query. Same deadline, same 401/2FA handling; no CSRF, because a
 * GET does not need one.
 */
export async function apiGet(url: string, options: ApiRequestOptions = {}): Promise<Response> {
  return apiRequest("GET", url, undefined, options);
}

/**
 * GET and parse, through the same door. For a page moving a raw `fetch` into
 * something with a deadline, 401/2FA handling and readable errors:
 *
 *   queryFn: ({ signal }) => fetchJson<Contact[]>(`/api/contacts?projectId=${id}`, { signal })
 *
 * Pass React Query's `signal` so a query that is no longer wanted stops
 * downloading. A 204 resolves `undefined`.
 */
export async function fetchJson<T = unknown>(url: string, options: ApiRequestOptions = {}): Promise<T> {
  const res = await apiGet(url, options);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/* ─────────────────────────── session endings ─────────────────────────── */

// Global flag to prevent multiple simultaneous logout redirects
let isRedirectingToLogout = false;

/**
 * The server enforces 2FA on every authenticated /api route (server/middleware/
 * require2FA.ts) and answers 403 `2FA_REQUIRED` when the session has not
 * cleared it. That is now met in place by the step-up dialog; this redirect is
 * what happens when no dialog is mounted to ask.
 */
let isRedirectingTo2FA = false;

async function is2FARequired(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  const { code, message } = await peekBody(res);
  return code === "2FA_REQUIRED" || /2FA_REQUIRED/.test(message);
}

function redirectTo2FA() {
  if (isRedirectingTo2FA || isRedirectingToLogout) return;
  if (isOnPublicRoute()) return;
  if (window.location.pathname === "/verify") return;

  isRedirectingTo2FA = true;
  // Where they were, so verifying brings them back rather than to /home.
  rememberReturnTo();
  // A full navigation, not SPA routing: AuthContext is holding a cached
  // twoFactorStatus that says `required: false`. Reloading re-reads the real
  // status from the server instead of trusting that.
  window.location.assign("/verify");
}

export function isLoggingOut() {
  return isRedirectingToLogout;
}

/**
 * A sign-out the app started itself (AuthContext.logout) is under way: it owns
 * the navigation to the sign-in page, and the note that says why.
 *
 * Without this, the idle sign-out lost its explanation (QA, 2026-10-28). The
 * reader comes back after an hour; the idle timer calls logout({ reason:
 * "idle" }), which clears "known signed in" and awaits the server — and in
 * that await the window-focus refetch gets its 401, finds nobody known signed
 * in (so records nothing), and redirectToLogin() navigates away to
 * /api/logout → /login?logout=success before logout() ever writes its note.
 * The sign-in page then had nothing to say. Now a 401 during our own sign-out
 * leaves the navigation, and the note, to it.
 */
export function markSigningOut() {
  isRedirectingToLogout = true;
}

// Public routes that should NOT trigger an auth redirect on 401
const PUBLIC_ROUTES = ["/login", "/forgot-password", "/reset-password", "/privacy-policy", "/terms-of-use"];

function isOnPublicRoute(): boolean {
  if (typeof window === "undefined") return true;
  const path = window.location.pathname;
  // Every public and buyer page from the one list (lib/publicPages.ts) —
  // /embed/ included: Console's wizard framed in a Showroom screen says
  // itself when its token ends, and a login page on a kiosk would not.
  return PUBLIC_ROUTES.some((route) => path === route || path.startsWith(route + "/")) || isPublicPagePath(path);
}

/**
 * The endpoints whose whole job is to ask "am I signed in?" (or to sign in).
 * A 401 from these is an answer, not a session ending.
 */
function isAuthProbe(url: string): boolean {
  return (
    url.includes("/api/auth/login") ||
    url.includes("/api/auth/2fa") ||
    url.includes("/api/auth/user") ||
    url.includes("/api/auth/passkeys/authenticate")
  );
}

/** A 401 carrying one of the session-policy codes. */
function isPolicyTimeout(code: string): boolean {
  return code === SESSION_IDLE_CODE || code === SESSION_ABSOLUTE_CODE;
}

/**
 * A 401 that means a session ENDED: say why on the sign-in page, remember
 * where they were, and go there.
 *
 * The reason is recorded only when there is something to explain — a policy
 * timeout, or a 401 after this tab had seen a signed-in session. A visitor who
 * was never signed in is owed no apology.
 */
async function handleSessionEnded(res: Response): Promise<void> {
  const { code } = await peekBody(res);
  if (code === SESSION_IDLE_CODE) recordSessionEnd("idle");
  else if (code === SESSION_ABSOLUTE_CODE) recordSessionEnd("expired");
  else if (isKnownSignedIn()) recordSessionEnd("ended");
  if (isPolicyTimeout(code) || isKnownSignedIn()) {
    // Lazy: sessionActivity imports apiRequest from this module.
    void import("./sessionActivity").then(({ resetSessionActivity }) => resetSessionActivity());
  }
  redirectToLogin();
}

export function redirectToLogin() {
  if (isRedirectingToLogout) return;
  // A 2FA redirect already in flight wins. It sends the user to /verify, the
  // step that actually unblocks them; logging them out here would tear down a
  // session that is still valid and make them start the whole sign-in over.
  if (isRedirectingTo2FA) return;
  if (isOnPublicRoute()) return;

  isRedirectingToLogout = true;
  // Before leaving, while the location still says where they were — signing
  // back in returns here (lib/sessionEnd.ts).
  rememberReturnTo();
  // Route through the server logout endpoint so the session is fully destroyed
  // before the login page loads. Without this, a still-valid session causes
  // checkSession() to reload the user and push them to the 2FA page instead of
  // the login page.
  window.location.replace("/api/logout");
}

/* ────────────────────────────── queries ────────────────────────────── */

type UnauthorizedBehavior = "returnNull" | "throw";

/**
 * The default query function. Passes React Query's abort signal, so a query
 * nobody is waiting for any more stops downloading, and goes through the same
 * deadline, CSRF-free GET, and step-up as `apiRequest`.
 *
 * Only the first key element is the URL — the rest are for cache uniqueness.
 */
export const getQueryFn: <T>(options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async (context) => {
    const { queryKey } = context;
    const url = queryKey[0] as string;
    /* Reading `context.signal` is what tells React Query to ABORT this fetch
       when the last observer unmounts; a remount then asks again. A dashboard
       widget that re-lays out (a column count settling, the gate opening)
       unmounts and remounts mid-request, and each time that cost a cancelled
       request and a second identical one (~10 per cold dashboard, QA
       2026-10-28). For the heavy aggregates the answer is worth finishing: it
       lands in the cache and the remounted widget joins it. Everything else
       still stops downloading when nobody wants it. */
    const signal = isHeavyQueryKey(queryKey) ? undefined : context.signal;

    const res = await execute("GET", url, undefined, false, {
      timeoutMs: REQUEST_TIMEOUT_MS,
      signal,
      stepUp: "passive",
    });

    // Still owes the second factor after the step-up was declined or could
    // not be shown. If a redirect is under way the page is leaving; otherwise
    // this is an error the screen should show — not an empty list.
    if (res.status === 403 && (await is2FARequired(res))) {
      if (isRedirectingTo2FA) return null;
      throw await errorFrom(res);
    }

    if (res.status === 401) {
      const { code } = await peekBody(res);

      // A policy timeout overrides `returnNull`, deliberately. Every query in
      // this app defaults to `on401: "returnNull"` and every call site defaults
      // its data to `[]`, so a session that ends while a background poll is in
      // flight would otherwise render as an app that has simply gone empty.
      if (isPolicyTimeout(code)) {
        await handleSessionEnded(res);
        return null;
      }

      if (unauthorizedBehavior === "returnNull") {
        // …and so does any 401 after this tab has seen a signed-in session.
        // `returnNull` exists so "not signed in YET" is absorbed quietly; a
        // session that was there and is not any more is a different fact.
        if (isKnownSignedIn() && !isAuthProbe(url) && !isOnPublicRoute()) {
          await handleSessionEnded(res);
        }
        return null;
      }

      // Session expired on a protected endpoint: redirect to login
      // (but don't redirect if we're already on a public route)
      if (!isAuthProbe(url)) {
        await handleSessionEnded(res);
        return null;
      }
    }

    if (!res.ok) {
      if (res.status === 429) notifyRateLimited("read");
      throw await errorFrom(res);
    }
    if (res.status === 204) return null;
    return await res.json();
  };

/* ─────────────────────────── cache behaviour ─────────────────────────── */

/**
 * Retry what a retry can fix.
 *
 * A 4xx is an answer — asking again does not change it, and on 429 it makes
 * things worse. A 500 is a fault the same request will hit again. What a retry
 * CAN fix is failing to reach the server (twice, backed off), a gateway that
 * was cold (502/503/504), and — once — a deadline, since each of those costs
 * the reader up to thirty seconds.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.kind === "network") return failureCount < 2;
  if (error.kind === "timeout") return failureCount < 1;
  if (error.status === 502 || error.status === 503 || error.status === 504) return failureCount < 2;
  return false;
}

export const retryDelay = (attempt: number) => Math.min(1000 * 2 ** attempt, 5000);

/**
 * Reads that are expensive enough that "refresh when the app comes back" must
 * not apply to them: the full contact list (every page of it), the external
 * ActiveCampaign API, the AI endpoints, and the report aggregates. They keep
 * the old five-minute freshness and do not refetch on focus. A query that sets
 * its own `staleTime` / `refetchOnWindowFocus` overrides either way.
 *
 * `[prefix, includesSubpaths]` — `/api/contacts` alone is the list; its
 * subpaths are single records and are cheap.
 */
export const HEAVY_QUERY_PREFIXES: ReadonlyArray<readonly [string, boolean]> = [
  ["/api/contacts", false],
  ["/api/contacts/insights", true],
  ["/api/activecampaign", true],
  ["/api/competitor-analysis", true],
  ["/api/intelligence", true],
  ["/api/reports", true],
  ["/api/usage/reports", true],
  ["/api/email-sync", true],
  /* The dashboard's widgets: one aggregate endpoint EACH, ~40 on a full
     layout. Refetched on every window focus they were most of a 46-request
     burst each time the reader came back to the tab (QA 2026-10-28), and the
     widgets that change with a write (tasks, offers, contacts) already
     invalidate their own keys. */
  ["/api/dashboard/metrics", true],
];

/** Heavy reads whose URL names a project, so a prefix cannot catch them. */
const HEAVY_QUERY_PATTERNS: readonly RegExp[] = [
  // The Email list report reads ActiveCampaign — an external API.
  /^\/api\/projects\/[^/]+\/email-list-report(?:[?/]|$)/,
];

export function isHeavyQueryKey(key: readonly unknown[]): boolean {
  const first = String(key[0] ?? "");
  return (
    HEAVY_QUERY_PREFIXES.some(
      ([prefix, subpaths]) =>
        first === prefix || first.startsWith(`${prefix}?`) || (subpaths && first.startsWith(`${prefix}/`)),
    ) || HEAVY_QUERY_PATTERNS.some((re) => re.test(first))
  );
}

export const DEFAULT_STALE_MS = 30_000;
export const HEAVY_STALE_MS = 5 * 60_000;

/**
 * How old an answer must be before COMING BACK to the app refetches it.
 *
 * Longer than DEFAULT_STALE_MS on purpose. The point of refetch-on-focus is
 * the phone that went in a pocket for an hour, not a reader who glanced at
 * another window: at thirty seconds every alt-tab refetched everything on
 * screen, and that is what turned ordinary use into "Too many requests".
 * Navigation still refetches at DEFAULT_STALE_MS, as before.
 */
export const FOCUS_REFETCH_AFTER_MS = 2 * 60_000;

/** The focus rule, exported for its test: heavy reads never; the rest once they are old. */
export function shouldRefetchOnFocus(query: Pick<Query, "queryKey" | "state">, now: number = Date.now()): boolean {
  if (isHeavyQueryKey(query.queryKey)) return false;
  return now - (query.state?.dataUpdatedAt ?? 0) >= FOCUS_REFETCH_AFTER_MS;
}

/**
 * A FAILED REQUEST USED TO LOOK EXACTLY LIKE AN EMPTY PROJECT.
 *
 * A query that threw left the page rendering its own default of nothing at all
 * — and every list page defaults its data to `[]`. Stopping the server and
 * opening the app made the point: Tasks rendered "Total 0" above "No tasks
 * yet", confidently and falsely. This says something once, centrally, so a page
 * that has not been given a bespoke error state still tells the truth.
 *
 * Throttled, because one dashboard load fires seventeen metric endpoints and an
 * outage would otherwise stack seventeen identical toasts.
 */
let lastQueryErrorNotice = 0;
function notifyQueryFailed(error: unknown) {
  const now = Date.now();
  if (now - lastQueryErrorNotice < 8_000) return;
  lastQueryErrorNotice = now;
  toast({
    variant: "destructive",
    title: isUnreachable(error)
      ? say(msg("shellErrorCouldntReachTitle", "Couldn't reach SaleFish"))
      : say(msg("shellErrorCouldntLoadTitle", "Couldn't load your data")),
    description: isUnreachable(error)
      ? say(msg("shellErrorMaybeOutOfDate", "Check your connection — what's on screen may be out of date."))
      : localizeApiMessage(describeApiError(error), say),
  });
}

/**
 * Statuses this app already routes somewhere better. 401 signs out, the 2FA
 * 403 raises the step-up prompt, 429 has its own notice. Toasting them again
 * would put a second message behind a navigation or a dialog.
 */
function isHandledElsewhere(error: unknown): boolean {
  const status = (error as ApiError)?.status;
  if (status === 402 && (error as ApiError)?.code === "CARD_REQUIRED") {
    // The company is locked for want of a card (shared/cardRule.ts). Re-read
    // the card status and CardGate takes over the screen. (Other 402s — a
    // plan change with no card yet — are ordinary errors with their own words.)
    queryClient.invalidateQueries({ queryKey: ["/api/card-status"] });
    return true;
  }
  return status === 401 || status === 403 || status === 429;
}

/**
 * How long the cache waits before toasting a failed read, so a panel that
 * draws its own LoadError for it has rendered and said so (lib/shownErrors.ts).
 */
const PANEL_GRACE_MS = 400;

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // `meta: { quiet: true }` on a query whose failure is expected and already
      // handled in the component — an optional probe, a capability check.
      if (query.meta?.quiet) return;
      if (isHandledElsewhere(error)) return;
      if (isLoggingOut()) return;
      // A panel showing its own LoadError for this failure has said it: no second word in a toast.
      setTimeout(() => {
        if (errorShownByPanel(error) || isLoggingOut()) return;
        notifyQueryFailed(error);
      }, PANEL_GRACE_MS);
    },
  }),
  mutationCache: new MutationCache({
    /**
     * The fallback failure path for writes. Twenty-five of the app's mutations
     * defined no `onError`, so a rejected write produced no message and no
     * visible change. A mutation that handles its own errors keeps doing so:
     * this defers to `mutation.options.onError` rather than toasting on top.
     */
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return;
      if (isHandledElsewhere(error)) return;
      if (isLoggingOut()) return;
      toast({
        variant: "destructive",
        title: say(msg("shellErrorDidntSaveTitle", "That didn't save")),
        description: localizeApiMessage(describeApiError(error), say),
      });
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "returnNull" }), // AuthContext handles auth state
      refetchInterval: false,
      /**
       * Refresh what is on screen when the app comes back. The installed app is
       * suspended, not reloaded, when somebody switches away — with this off, a
       * list showed whatever was loaded before the phone went in a pocket until
       * the reader navigated away and back. Only MOUNTED queries older than
       * FOCUS_REFETCH_AFTER_MS refetch, and the heavy reads (the dashboard's
       * widgets among them) never do: at "older than staleTime" a return to
       * the dashboard after half a minute was ~46 requests (QA 2026-10-28).
       */
      refetchOnWindowFocus: (query: Query) => shouldRefetchOnFocus(query),
      staleTime: (query: Query) => (isHeavyQueryKey(query.queryKey) ? HEAVY_STALE_MS : DEFAULT_STALE_MS),
      /**
       * Don't let the query layer decide we are offline and quietly stop.
       *
       * The default `networkMode: "online"` PAUSES a query while the browser
       * reports itself offline: `status: "pending"`, `fetchStatus: "paused"` —
       * no spinner a screen keyed on `isLoading` would show, no error, no data.
       * On a connection that keeps dropping that is a silent blank page.
       * "always" runs it and lets a failure be a failure the screen can show.
       * Screens should key loading on `isPending`, not `isLoading`.
       */
      networkMode: "always",
      retry: shouldRetryQuery,
      retryDelay,
    },
    mutations: {
      // Never replayed automatically — a write is not idempotent.
      retry: false,
      // A Save pressed offline fails visibly instead of pausing with no sign.
      networkMode: "always",
    },
  },
});

/* ──────────────────────────── helpers for pages ──────────────────────────── */

/**
 * Does this query key belong to the project? True when any part of the key IS
 * the id, or is a URL naming it as a path segment or a `projectId=` parameter.
 */
export function queryMentionsProject(key: readonly unknown[], projectId: string): boolean {
  const escaped = projectId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const param = new RegExp(`[?&]projectId=${escaped}(?:&|$)`);
  return key.some((part) => {
    if (part === projectId) return true;
    if (typeof part === "string") {
      return part.includes(`/${projectId}/`) || part.endsWith(`/${projectId}`) || param.test(part);
    }
    if (part && typeof part === "object") {
      return Object.values(part as Record<string, unknown>).some((v) => v === projectId);
    }
    return false;
  });
}

/**
 * Everything on screen that a change to one project can move, in one call —
 * instead of a list of individual keys that is always one short. Only mounted
 * queries refetch; the rest are marked stale for their next mount.
 */
export function invalidateProject(projectId: string | null | undefined, client: QueryClient = queryClient) {
  if (!projectId) return Promise.resolve();
  return client.invalidateQueries({ predicate: (q) => queryMentionsProject(q.queryKey, projectId) });
}

/**
 * Keep the rows on screen while a filter, search or page change loads the next
 * set, instead of flashing an empty table or a skeleton between them:
 *
 *   useQuery({ queryKey: [url, filters], ...keepPreviousRows })
 *
 * `isPlaceholderData` is true while the old rows are showing.
 */
export { keepPreviousData };
export const keepPreviousRows = { placeholderData: keepPreviousData } as const;
